<template>
  <div class="actions">
    <section v-for="(action, index) in value" :key="index" class="action">
      <div class="action-header">
        <h3>Action {{ index + 1 }}</h3>
        <div v-if="isEditing" class="action-controls">
          <Button variant="text" :disabled="index === 0" title="Move action up" @click="moveAction(index, -1)">
            <MdIcon name="chevron-up" />
          </Button>
          <Button variant="text" :disabled="index === value.length - 1" title="Move action down" @click="moveAction(index, 1)">
            <MdIcon name="chevron-down" />
          </Button>
          <Button variant="text" color="danger" :disabled="value.length === 1" @click="removeAction(index)">Remove</Button>
        </div>
      </div>

      <template v-if="isEditing">
        <div class="row">
          <Multiselect
            :value="selectedAction(action)"
            label="Action"
            :options="actionOptions"
            :get-label-callback="a => a.label"
            @input="onActionSelect(index, $event)"
          />
          <div class="description">{{ description(action) }}</div>
        </div>
        <template v-if="actionId(action) === actionTypes.setField">
          <div class="row">
            <Multiselect label="Field" :value="action.field" :options="fieldOptions" @input="onFieldChange(index, $event)" />
          </div>
          <div class="row">
            <TextField
              v-if="action.field && fieldSettings(action) && (fieldSettings(action).type === 'string' || fieldSettings(action).type === 'number')"
              label="Value"
              :value="action.value"
              :settings="fieldSettings(action)"
              @input="updateAction(index, { ...action, value: $event })"
            />
            <DatePicker
              v-if="action.field && fieldSettings(action) && fieldSettings(action).type === 'date'"
              label="Value"
              allow-file-date
              :value="action.value"
              :settings="fieldSettings(action)"
              @input="updateAction(index, { ...action, value: $event })"
            />
            <Multiselect
              v-if="action.field && fieldSettings(action) && fieldSettings(action).type === 'boolean'"
              label="Value"
              :value="action.value"
              :options="['true', 'false']"
              @input="updateAction(index, { ...action, value: $event })"
            />
          </div>
        </template>
        <template v-if="actionId(action) === actionTypes.runPrompt">
          <PromptSelector
            :is-editing="isEditing"
            :value="action.promptOptions"
            :current-action-index="index"
            @input="updateAction(index, { ...action, promptOptions: $event })"
          />
          <div class="row">
            <Checkbox
              :value="action.promptOptions ? action.promptOptions.updateIndex !== false : true"
              label="Update portfolio index with prompt result"
              @input="updatePromptOptions(index, action, { updateIndex: $event })"
            />
          </div>
          <div v-if="action.promptOptions ? action.promptOptions.updateIndex !== false : true" class="row">
            <Multiselect
              label="Index fields"
              :value="action.promptOptions && action.promptOptions.indexFields ? action.promptOptions.indexFields : []"
              :options="indexFieldOptions(action)"
              :options-limit="500"
              :multiple="true"
              :taggable="true"
              :multiselect="true"
              placeholder="Entire result"
              tag-placeholder="Press Enter to add a field"
              @tag="onIndexFieldTag(index, action, $event)"
              @input="updatePromptOptions(index, action, { indexFields: $event && $event.length ? $event : [] })"
            />
          </div>
        </template>
        <template v-if="actionId(action) === actionTypes.generateDocument">
          <div class="row">
            <PromptSelector
              :is-editing="isEditing"
              :value="action.documentOptions"
              :current-action-index="index"
              @input="updateAction(index, { ...action, documentOptions: $event })"
            />
          </div>
          <div class="row filename">
            <CommonTextField
              label="Document name"
              :value="action.documentOptions ? action.documentOptions.fileName : ''"
              @input="updateDocumentOptions(index, action, { fileName: $event })"
            />
          </div>
          <div class="row">
            <Checkbox
              :value="action.documentOptions ? action.documentOptions.allowMultiple : false"
              label="Allow multiple copies"
              @input="updateDocumentOptions(index, action, { allowMultiple: $event })"
            />
          </div>
        </template>
        <template v-if="actionId(action) === actionTypes.mapPrompt">
          <div class="row">
            <Multiselect
              label="Items from"
              :value="action.mapPromptOptions ? action.mapPromptOptions.itemsFrom : null"
              :options="itemsFromOptions(index)"
              @input="onItemsFromChange(index, action, $event)"
            />
          </div>
          <div v-if="isFamilyRelation(action.mapPromptOptions && action.mapPromptOptions.itemsFrom)" class="row">
            <Multiselect
              label="Fields"
              :value="action.mapPromptOptions && action.mapPromptOptions.familyFields ? action.mapPromptOptions.familyFields : []"
              :options="portfolioFieldOptions"
              :multiple="true"
              :multiselect="true"
              placeholder="Select fields"
              @input="updateMapPromptOptions(index, action, { familyFields: $event || [] })"
            />
          </div>
          <div class="row item-variable-name">
            <CommonTextField
              label="Item variable name"
              :value="action.mapPromptOptions ? action.mapPromptOptions.itemVariableName || 'item' : 'item'"
              @input="updateMapPromptOptions(index, action, { itemVariableName: $event || 'item' })"
            />
          </div>
          <PromptSelector
            :is-editing="isEditing"
            :value="action.mapPromptOptions"
            :current-action-index="index"
            @input="updateAction(index, { ...action, mapPromptOptions: $event })"
          />
        </template>
      </template>

      <template v-else>
        <div v-if="actionId(action) === actionTypes.setField" class="row">Field "{{ action.field }}" will be set to "{{ action.value }}"</div>
        <template v-if="actionId(action) === actionTypes.runPrompt">
          <PromptSelector
            :is-editing="isEditing"
            :value="action.promptOptions"
            :current-action-index="index"
            :message="action.promptOptions && action.promptOptions.updateIndex === false ? 'Index will not be updated; result of' : 'Index will be updated with result of'"
          />
          <Checkbox
            :disabled="true"
            :value="action.promptOptions ? action.promptOptions.updateIndex !== false : true"
            label="Update portfolio index with prompt result"
          />
          <div v-if="action.promptOptions ? action.promptOptions.updateIndex !== false : true" class="row">
            Index fields: {{ indexFieldsSummary(action) }}
          </div>
        </template>
        <template v-if="actionId(action) === actionTypes.generateDocument">
          <PromptSelector
            :is-editing="isEditing"
            :value="action.documentOptions"
            :current-action-index="index"
            message="Document will be generated based on"
          />
          <CommonTextField :disabled="true" label="Document name" :value="action.documentOptions ? action.documentOptions.fileName : ''" />
          <Checkbox :disabled="true" :value="action.documentOptions ? action.documentOptions.allowMultiple : false" label="Allow multiple copies" />
        </template>
        <template v-if="actionId(action) === actionTypes.mapPrompt">
          <div class="row">Items from: {{ mapItemsFromSummary(action) }}</div>
          <div v-if="isFamilyRelation(action.mapPromptOptions && action.mapPromptOptions.itemsFrom)" class="row">
            Fields: {{ mapFamilyFieldsSummary(action) }}
          </div>
          <div class="row">
            Item variable: {{ action.mapPromptOptions ? action.mapPromptOptions.itemVariableName || 'item' : 'item' }}
          </div>
          <PromptSelector
            :is-editing="isEditing"
            :value="action.mapPromptOptions"
            :current-action-index="index"
            message="Prompt will run on each item using"
          />
        </template>
      </template>
    </section>

    <Button v-if="isEditing" color="primary" @click="addAction">Add action</Button>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Multiselect from '@/components/common/Multiselect.vue';
import TextField from './filter-controls/TextField.vue';
import DatePicker from './filter-controls/DatePicker.vue';
import PromptSelector from './PromptSelector.vue';
import Checkbox from '@/components/common/Checkbox.vue';
import CommonTextField from '@/components/common/TextField.vue';
import Button from '@/components/common/Button.vue';
import MdIcon from '@/components/common/MdIcon.vue';

const ACTION_TYPES = {
  setField: 'set-field',
  runPrompt: 'run-prompt',
  generateDocument: 'generate-document',
  mapPrompt: 'map-prompt'
};

const FAMILY_RELATION_OPTIONS = ['parents', 'children'];

const actionOptions = [
  { id: ACTION_TYPES.setField, label: 'Set value of a field' },
  { id: ACTION_TYPES.runPrompt, label: 'Run prompt and set values' },
  { id: ACTION_TYPES.generateDocument, label: 'Generate a document' },
  { id: ACTION_TYPES.mapPrompt, label: 'Run prompt on each item in array' }
];

const createBlankAction = () => ({
  promptOptions: null,
  field: null,
  value: null,
  documentOptions: null,
  mapPromptOptions: null
});

const deriveActionId = action => {
  if (action && action.mapPromptOptions) {
    return ACTION_TYPES.mapPrompt;
  }
  if (action && action.documentOptions) {
    return ACTION_TYPES.generateDocument;
  }
  if (action && action.promptOptions) {
    return ACTION_TYPES.runPrompt;
  }
  return ACTION_TYPES.setField;
};

export default {
  name: 'Actions',
  components: {
    Multiselect,
    TextField,
    DatePicker,
    PromptSelector,
    Checkbox,
    CommonTextField,
    Button,
    MdIcon
  },
  props: {
    value: {
      type: Array,
      default: () => [createBlankAction()]
    },
    isEditing: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      actionOptions,
      actionTypes: ACTION_TYPES
    };
  },
  computed: {
    ...mapState({
      filterSettings: state => state.pipelines.settings
    }),
    fieldOptions() {
      if (!this.filterSettings) {
        return [];
      }
      const settings = this.filterSettings.find(setting => setting.database === 'applications');
      if (!settings) {
        return [];
      }
      const fieldEntries = Object.entries(settings.fields);
      return fieldEntries
        .filter(([key, value]) => {
          return value.type === 'string' || value.type === 'number' || value.type === 'boolean' || value.type === 'date';
        })
        .map(([key]) => key);
    },
    portfolioFieldOptions() {
      if (!this.filterSettings) {
        return [];
      }
      const settings = this.filterSettings.find(setting => setting.database === 'applications');
      if (!settings) {
        return [];
      }
      return Object.keys(settings.fields);
    }
  },
  methods: {
    actionId(action) {
      return deriveActionId(action);
    },
    selectedAction(action) {
      return this.actionOptions.find(option => option.id === this.actionId(action)) || null;
    },
    fieldSettings(action) {
      if (!this.filterSettings) {
        return null;
      }
      const settings = this.filterSettings.find(setting => setting.database === 'applications');
      if (!settings || !action.field) {
        return null;
      }
      return settings.fields[action.field];
    },
    description(action) {
      switch (this.actionId(action)) {
        case this.actionTypes.setField:
          return 'Set the value of a field in the document. No prompt will run, and no document will be generated.';
        case this.actionTypes.runPrompt:
          return 'Run a prompt and set values based on the output.';
        case this.actionTypes.generateDocument:
          return 'Generate a document based on the specified options.';
        case this.actionTypes.mapPrompt:
          return 'Resolve an array and run a prompt on each item. Result is an array of prompt outputs.';
        default:
          return '';
      }
    },
    isFamilyRelation(value) {
      return FAMILY_RELATION_OPTIONS.includes(value);
    },
    itemsFromOptions(actionIndex) {
      const priorActions = Array.from({ length: actionIndex }, (value, index) => `{{action.${index + 1}}}`);
      return [...FAMILY_RELATION_OPTIONS, ...priorActions];
    },
    mapItemsFromSummary(action) {
      return (action.mapPromptOptions && action.mapPromptOptions.itemsFrom) || 'not set';
    },
    mapFamilyFieldsSummary(action) {
      const fields = action.mapPromptOptions && action.mapPromptOptions.familyFields;
      return fields && fields.length ? fields.join(', ') : 'all fields';
    },
    updateAction(index, newAction) {
      this.$emit(
        'input',
        this.value.map((action, actionIndex) => (actionIndex === index ? newAction : action))
      );
    },
    updateDocumentOptions(index, action, changes) {
      this.updateAction(index, {
        ...action,
        documentOptions: { ...action.documentOptions, ...changes }
      });
    },
    updatePromptOptions(index, action, changes) {
      this.updateAction(index, {
        ...action,
        promptOptions: { ...action.promptOptions, ...changes }
      });
    },
    updateMapPromptOptions(index, action, changes) {
      this.updateAction(index, {
        ...action,
        mapPromptOptions: { ...action.mapPromptOptions, ...changes }
      });
    },
    onItemsFromChange(index, action, itemsFrom) {
      const changes = { itemsFrom: itemsFrom || null };
      if (!this.isFamilyRelation(itemsFrom)) {
        changes.familyFields = [];
      }
      this.updateMapPromptOptions(index, action, changes);
    },
    indexFieldsSummary(action) {
      const fields = action.promptOptions && action.promptOptions.indexFields;
      return fields && fields.length ? fields.join(', ') : 'entire result';
    },
    indexFieldOptions(action) {
      const selected = (action.promptOptions && action.promptOptions.indexFields) || [];
      const extras = selected.filter(field => field && !this.fieldOptions.includes(field));
      return extras.length ? [...this.fieldOptions, ...extras] : this.fieldOptions;
    },
    onIndexFieldTag(index, action, tag) {
      const field = typeof tag === 'string' ? tag.trim() : '';
      if (!field) {
        return;
      }
      const current = (action.promptOptions && action.promptOptions.indexFields) || [];
      if (current.includes(field)) {
        return;
      }
      this.updatePromptOptions(index, action, { indexFields: [...current, field] });
    },
    onActionSelect(index, option) {
      const actionId = option ? option.id : ACTION_TYPES.setField;
      const action = createBlankAction();
      if (actionId === ACTION_TYPES.runPrompt) {
        action.promptOptions = { promptId: null, variables: [], updateIndex: true, indexFields: [] };
      } else if (actionId === ACTION_TYPES.generateDocument) {
        action.documentOptions = { promptId: null, variables: [], fileName: '', allowMultiple: false };
      } else if (actionId === ACTION_TYPES.mapPrompt) {
        action.mapPromptOptions = {
          promptId: null,
          variables: [],
          itemsFrom: null,
          familyFields: [],
          itemVariableName: 'item',
          contextFromAction: null
        };
      }
      this.updateAction(index, action);
    },
    onFieldChange(index, newField) {
      this.updateAction(index, { ...createBlankAction(), field: newField });
    },
    addAction() {
      this.$emit('input', [...this.value, createBlankAction()]);
    },
    removeAction(index) {
      if (this.value.length <= 1) {
        return;
      }
      this.$emit(
        'input',
        this.value.filter((action, actionIndex) => actionIndex !== index)
      );
    },
    moveAction(index, offset) {
      const targetIndex = index + offset;
      if (targetIndex < 0 || targetIndex >= this.value.length) {
        return;
      }
      const actions = [...this.value];
      const [action] = actions.splice(index, 1);
      actions.splice(targetIndex, 0, action);
      this.$emit('input', actions);
    }
  }
};
</script>
<style lang="scss" scoped>
.actions {
  .action {
    padding: 15px;
    margin-bottom: 15px;
    border: 1px solid var(--theme-highlight);
    border-radius: 4px;
  }

  .action-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    h3 {
      margin: 0;
      font-size: 1rem;
    }
  }

  .action-controls {
    display: flex;
    align-items: center;
  }

  .row {
    margin-bottom: 10px;
  }

  .description {
    font-size: 12px;
    font-style: italic;
  }
}

.filename,
.item-variable-name {
  display: grid;
  width: 300px;
  grid-template-columns: 1fr;
}
</style>
